import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  useAnimation,
} from "motion/react";
import { toast } from "sonner";

import { orbit } from "ldrs";
orbit.register();

const PULL_CONFIG = {
  threshold: 70,
  maxDistance: 105,
  resistance: 0.5,
};

const ANIMATION_CONFIG = {
  refresh: {
    type: "spring",
    stiffness: 350,
    damping: 35,
  },
  reset: {
    type: "spring",
    stiffness: 280,
    damping: 30,
  },
};

const PullToRefresh = ({ onRefresh, children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const dragY = useMotionValue(0);
  const controls = useAnimation();
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const rafId = useRef(null);

  const pullProgress = useTransform(dragY, [0, PULL_CONFIG.threshold], [0, 1], {
    clamp: true,
  });

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      await controls.start({
        y: PULL_CONFIG.threshold,
        transition: ANIMATION_CONFIG.refresh,
      });
      await onRefresh();
    } catch (error) {
      toast.error("Failed to refresh content");
    } finally {
      await controls.start({
        y: 0,
        transition: ANIMATION_CONFIG.reset,
      });
      setIsRefreshing(false);
    }
  }, [controls, onRefresh, isRefreshing]);

  useEffect(() => {
    const content = contentRef.current;
    const container = containerRef.current;
    if (!content || !container) return;

    const checkPosition = () => {
      setIsAtTop(content.getBoundingClientRect().top >= 40);
      rafId.current = requestAnimationFrame(checkPosition);
    };

    rafId.current = requestAnimationFrame(checkPosition);

    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      if (!isAtTop || isRefreshing) return;
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (!isAtTop || isRefreshing || !touchStartY.current) return;

      const deltaY = e.touches[0].clientY - touchStartY.current;
      const deltaX = Math.abs(e.touches[0].clientX - touchStartX.current);

      // Horizontal swipe — let the child element handle it
      if (deltaX > Math.abs(deltaY)) return;

      if (deltaY > 0) {
        e.preventDefault();
        const pullDistance = Math.min(
          deltaY * PULL_CONFIG.resistance,
          PULL_CONFIG.maxDistance
        );
        controls.set({ y: pullDistance });
        dragY.set(pullDistance);
      }
    };

    const handleTouchEnd = async () => {
      if (!touchStartY.current || isRefreshing) return;

      touchStartY.current = 0;
      const currentPull = dragY.get();

      if (currentPull >= PULL_CONFIG.threshold) {
        handleRefresh();
      } else {
        await controls.start({
          y: 0,
          transition: ANIMATION_CONFIG.reset,
        });
        dragY.set(0);
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isRefreshing, isAtTop, controls, dragY, handleRefresh]);

  return (
    <>
      <div className="loading-wrapper">
        <motion.div
          className="loading-indicator"
          style={{ opacity: pullProgress }}
        >
          <l-orbit size="35" speed="1.35" stroke="2" color="white" />
        </motion.div>
      </div>
      <motion.div
        className="pull-to-refresh"
        ref={containerRef}
        animate={controls}
        style={{ y: dragY }}
        initial={false}
        dragConstraints={{ top: 0, bottom: PULL_CONFIG.maxDistance }}
        dragElastic={false}
      >
        <motion.div
          className="pulldown-container content-container"
          id="pulldown-container"
          ref={contentRef}
        >
          {children}
        </motion.div>
      </motion.div>
    </>
  );
};

export default PullToRefresh;
